import React, { MouseEvent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { v4 as uuid_v4 } from 'uuid';
import type { AnyObjectSchema } from 'yup';
import { customOfferLeLiveSchema, editableCustomOfferLeLiveSchema } from '~/validation/schemas/customOffers';

import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getRegionByCurrency } from '@luxuryescapes/lib-regions';

import DateWidget from '~/components/Common/Elements/DateWidget';
import OrderDetailNotes from '~/components/Purchases/OrderDetail/OrderDetailNotes';

import { OPERATIONS_SUPPORT } from '~/consts/notes';
import stores from '~/consts/stores';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useToggleState from '~/hooks/useToggleState';

import OffersService from '~/services/OffersService';
import * as OrdersService from '~/services/OrdersService';
import { addDays, formatDateISO, isBefore, newDate } from '~/services/TimeService';

import { centsToDollars, dollarsToCents, floatify } from '~/utils/mathUtils';

import { RichTextEditor } from '../Forms/fields';

import V2CustomerPaymentFields from './CustomOfferForm/V2CustomerPaymentFields';
import CustomOfferInternalDiscountField from './CustomOfferInternalDiscountField';
import CustomOfferLoyaltyCalculator from './CustomOfferLoyaltyCalculator';
import CustomOfferMerchantFeeField from './CustomOfferMerchantFeeField';
import { CustomOfferRequiredFieldsLegend } from './CustomOfferRequiredFieldsLegend';
import { TravellersSection, TravellersSectionRef } from './CustomOfferTravellersSection';
import { LE_ENTITY_AUS, OFFER_TYPES } from './constants';
import { getOfferTypeSelectOptions, isOfferTypeSelectLocked } from './customOfferPackagePermissions';
import { ConfirmDeleteDialog } from './dialogs/ConfirmDeleteDialog';
import { buildPendingPointsPayload, fetchLoyaltyTier } from './loyaltyUtils';
import {
  PaymentFlowType,
  PaymentFlowV2FieldErrorMap,
  buildCustomerPaymentMetadata,
  calculatePaymentFlowV2DerivedAmounts,
  deriveDueDateFromPaymentFlowV2,
  hydratePaymentFlowV2FormValues,
} from './paymentFlowUtils';
import { getIsChangedFromLeLiveOffer } from './utils';

const LE_LIVE_ITEM_TYPE: App.CustomOfferType = 'le_live';
const LE_LIVE_SALES_ATTRIBUTION = stores.le_live.utmMedium;
const LE_LIVE_CUSTOM_OFFER_IMAGE_ID = 'le_live_custom_offer_v4';
const DEFAULT_SUPPLIER = 'Other';
const LE_LIVE_CUSTOMER_CURRENCIES = ['AUD'];

export type CustomOfferLeLiveFormProps = {
  user: App.User;
  currentOffer?: App.CustomOffer;
  hasOrders?: boolean;
  order?: App.Order;
  onUpdate?: (offer: App.CustomOffer) => Promise<void>;
  paymentDate?: string;
  changeFormType: (
    type: App.CustomOfferType,
    internalDiscountTracking?: string | null,
    nextVendorName?: string,
  ) => void;
  offerTypes: typeof OFFER_TYPES;
  brand: App.Brands;
  travellersSectionRef: RefObject<TravellersSectionRef>;
  isBusinessTraveller: boolean;
  customerHasPaid: boolean;
};

const buildDefaultValues = (user: App.User, offer?: App.CustomOffer): CustomOfferFormValues => {
  const today = new Date().toISOString().split('T')[0];
  const item = offer?.items[0];
  const travellers = offer?.items[0]?.travellers;
  const effectiveItemType: App.CustomOfferType = item?.type ?? LE_LIVE_ITEM_TYPE;
  const paymentFlowV2 = offer?.payment_metadata?.payment_flow_v2;
  const hydratedPaymentFlowV2 = hydratePaymentFlowV2FormValues(paymentFlowV2, !offer);

  return {
    item: {
      type: effectiveItemType,
      name: item?.name ?? '',
      description: item?.description ?? '',
      vendor: item?.vendor ?? '',
      vendor_reference_number: item?.vendor_reference_number ?? '',
      salesforce_case_number: item?.salesforce_case_number ?? '',
      service_information: item?.service_information ?? '',
      start_date: item?.start_date ?? today,
      end_date: item?.end_date ?? formatDateISO(addDays(1)),
    },
    travellers:
      travellers?.length > 0
        ? travellers
        : [
            {
              name: '',
              type: 'adult',
              country: '',
              date_of_birth: null,
            },
          ],
    price: offer?.price ? centsToDollars(offer.price) : 0,
    price_currency: offer?.price_currency ?? 'AUD',
    cost_price: 0,
    cost_price_currency: offer?.price_currency ?? offer?.cost_price_currency ?? 'AUD',
    due_date: deriveDueDateFromPaymentFlowV2(hydratedPaymentFlowV2, offer?.due_date),
    sold_at: today,
    payment_terms: 'full_payment',
    images: offer?.images ?? [],
    payment_metadata: {
      margin: offer?.payment_metadata?.margin ? centsToDollars(offer.payment_metadata.margin) : 0,
      le_service_fee: 0,
      service_fee_type: 'No fee',
      payment_type: 'full_payment',
      customer_exchange_rate: 1,
      customer_deposit_1_amount: 0,
      customer_deposit_1_due_date: null,
      deposit_pi_number_customer: null,
      final_amount_customer: offer?.price ? centsToDollars(offer.price) : 0,
      payment_flow_v2: hydratedPaymentFlowV2,
      block_customer_payment_notifications: true,
      merchant_fee_enabled: offer?.payment_metadata?.merchant_fee_enabled ?? true,
      vendor_payment_type: 'full_payment',
      vendor_exchange_rate: offer?.payment_metadata?.vendor_exchange_rate ?? 1,
      vendor_due_date: null,
      vendor_was_paid: false,
      vendor_deposit_1_amount: null,
      vendor_deposit_1_due_date: null,
      final_payment_vcc_digits: null,
      deposit_vcc_digits: null,
      final_amount_vendor: 0,
      vendor_payment_date: null,
      vendor_deposit_payment_date: null,
      le_entity: offer?.payment_metadata?.le_entity ?? LE_ENTITY_AUS,
      sales_attribution: LE_LIVE_SALES_ATTRIBUTION,
      created_by: offer?.payment_metadata?.created_by ?? '',
      sales_agent_name: offer?.payment_metadata?.sales_agent_name ?? user.fullName ?? '',
      internal_discount_tracking: offer?.payment_metadata?.internal_discount_tracking ?? '',
    },
    notes: offer?.notes ?? '',
    created_from_item_id: offer?.created_from_item_id ?? null,
    customer_phone_prefix: '',
    customer_phone_number: '',
    reason: '',
    reasonOther: '',
    supplier: DEFAULT_SUPPLIER,
    hiddenFromCustomer: offer?.hidden_from_customer ?? false,
    pdf_filename: offer?.pdf_filename ?? null,
    pdf_s3_key: offer?.pdf_s3_key ?? null,
    pdf_download_url: offer?.pdf_download_url ?? null,
  };
};

type Payload = Omit<App.CustomOffer, 'id' | 'status' | 'has_vcc' | 'created_at' | 'updated_at'> & {
  pdf?: {
    filename: string;
    content: string;
    mimeType: string;
  };
};

const buildPayload = (user: App.User, formData: CustomOfferFormValues): Payload => {
  const { item, travellers, payment_metadata, pdf, ...rest } = formData;

  const salesAgentName = payment_metadata.sales_agent_name?.trim() || user.fullName || '';
  const normalizedVendor = item.vendor?.trim() || DEFAULT_SUPPLIER;
  const customerPaymentMetadata = buildCustomerPaymentMetadata(payment_metadata);

  const customerTotalDollars = floatify(Number(payment_metadata.final_amount_customer ?? rest.price ?? 0));
  const customerTotalCents = dollarsToCents(customerTotalDollars);

  return {
    ...rest,
    purchase_type: 'sales',
    reason: undefined,
    customer_phone_prefix: '',
    customer_phone_number: '',
    price: customerTotalCents,
    cost_price: 0,
    due_date: deriveDueDateFromPaymentFlowV2(
      payment_metadata.payment_flow_v2,
      rest.due_date != null && rest.due_date !== '' ? formatDateISO(rest.due_date) : undefined,
    ),
    sold_at: formatDateISO(rest.sold_at),
    payment_metadata: {
      ...payment_metadata,
      vendor_due_date: null,
      final_amount_customer: customerTotalCents,
      ...customerPaymentMetadata,
      final_amount_vendor: 0,
      le_service_fee: 0,
      margin: dollarsToCents(Number(payment_metadata.margin)),
      le_entity: payment_metadata?.le_entity,
      sales_attribution: LE_LIVE_SALES_ATTRIBUTION,
      sales_agent_name: salesAgentName,
      block_customer_payment_notifications: true,
      additional_vendor_payment: payment_metadata.additional_vendor_payment
        ? dollarsToCents(Number(payment_metadata.additional_vendor_payment))
        : 0,
      additional_margin:
        payment_metadata.additional_margin != null ? dollarsToCents(Number(payment_metadata.additional_margin)) : null,
      reason_additional_info: payment_metadata?.reason_additional_info,
      reason_issue_raised: payment_metadata?.reason_issue_raised,
      internal_discount_tracking: payment_metadata.internal_discount_tracking?.trim()
        ? payment_metadata.internal_discount_tracking.trim()
        : null,
    },
    name: item.name,
    fk_customer_id: user.id_member,
    customer_full_name: user.fullName,
    images: [],
    items: [
      {
        ...item,
        vendor: normalizedVendor,
        start_date: formatDateISO(item.start_date),
        end_date: formatDateISO(item.end_date),
        travellers: travellers.map((traveller) => ({
          ...traveller,
          name: traveller.name?.trim() === '' ? null : traveller.name,
          date_of_birth: traveller.date_of_birth ? formatDateISO(traveller.date_of_birth) : null,
        })),
      },
    ],
    ...(pdf?.content && { pdf: { filename: pdf.filename, content: pdf.content, mimeType: pdf.mimeType } }),
  };
};

const buildPaymentPayload = async (user: App.User, offer: App.CustomOffer, brand: App.Brands = 'luxuryescapes') => {
  const transactionKey = uuid_v4();
  const paymentFlowType = offer.payment_metadata.payment_flow_v2?.type;
  const total =
    paymentFlowType === 'manual'
      ? floatify(Number(offer.payment_metadata.payment_flow_v2?.manual_balance_amount ?? 0))
      : floatify(Number(offer.price ?? 0) / 100);
  const regionCode = getRegionByCurrency(offer.price_currency, brand);

  const pendingPoints = await buildPendingPointsPayload(user, offer, transactionKey, LE_LIVE_SALES_ATTRIBUTION, brand);
  const tier = pendingPoints ? await fetchLoyaltyTier(user.id_member) : null;

  return {
    brand,
    currency_code: offer.price_currency,
    region_code: regionCode.code,
    customer_id: user.id_member,
    customer_email: user.email,
    customer_phone_prefix: '',
    customer_phone: '',
    utm_source: brand,
    utm_medium: LE_LIVE_SALES_ATTRIBUTION,
    transaction_key: transactionKey,
    type: 'custom_offer',
    items: offer.items.map((item) => ({
      ...item,
      transaction_key: transactionKey,
      type: 'custom_offer',
      custom_offer_id: offer.id,
      due_date: offer.due_date,
      total,
      custom_item_type: item.type,
    })),
    ...(pendingPoints && tier && { lux_loyalty: { pendingPoints, tier } }),
  };
};

export default function CustomOfferLeLiveForm({
  user,
  hasOrders,
  order,
  onUpdate,
  currentOffer,
  changeFormType,
  offerTypes,
  brand,
  travellersSectionRef,
  isBusinessTraveller,
  customerHasPaid,
  paymentDate: _paymentDate,
}: CustomOfferLeLiveFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { user: currentAgent } = useCurrentUser();

  const [offer, setOffer] = useState<App.CustomOffer | undefined>(currentOffer);
  const [inclusionsDiscussed, setInclusionsDiscussed] = useState(offer?.status === 'approved');
  const [termsExplained, setTermsExplained] = useState(offer?.status === 'approved');
  const [sendStatus, setSendStatus] = useState<Utils.ProcessingState>('idle');

  const { isToggled: deleteDialogOpen, toggleOn: openDeleteDialog, toggleOff: closeDeleteDialog } = useToggleState();

  const { push, replace } = useAppNavigation();

  const isEditMode = !!offer?.id;

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
    watch,
    clearErrors,
    setValue,
    getValues,
  } = useForm<CustomOfferFormValues>({
    defaultValues: buildDefaultValues(user, offer),
    context: { editMode: isEditMode },
    resolver: yupResolver(
      (hasOrders ? editableCustomOfferLeLiveSchema : customOfferLeLiveSchema) as unknown as AnyObjectSchema,
    ),
    reValidateMode: 'onChange',
  });
  const paymentFlowV2Errors = errors.payment_metadata?.payment_flow_v2 as PaymentFlowV2FieldErrorMap | undefined;

  const itemType = watch('item.type');
  const offerTypeSelectOptions = useMemo(
    () => getOfferTypeSelectOptions(offerTypes, currentAgent, itemType),
    [offerTypes, currentAgent, itemType],
  );
  const isOfferTypeSelectDisabled = isSubmitting || isOfferTypeSelectLocked(currentAgent, itemType);
  const priceCurrencyCode = watch('price_currency');
  const price = watch('price');
  const depositFlow: PaymentFlowType = watch('payment_metadata.payment_flow_v2.type');
  const v2DepositAmount = watch('payment_metadata.payment_flow_v2.deposit_amount');
  const manualV2DepositAmount = watch('payment_metadata.payment_flow_v2.manual_deposit_amount');
  const rawSell = Number(watch('payment_metadata.final_amount_customer') ?? price) || 0;
  const customerSellForLoyalty = Number.isFinite(rawSell) ? floatify(rawSell) : 0;
  const paymentDeadline = watch('payment_metadata.payment_flow_v2.payment_deadline');
  const balanceDueDate = watch('payment_metadata.payment_flow_v2.balance_due_date');
  const manualBalanceDueDate = watch('payment_metadata.payment_flow_v2.manual_balance_due_date');

  useEffect(() => {
    const v2 = getValues('payment_metadata.payment_flow_v2');
    const raw = getValues('due_date');
    const formDue = raw != null && raw !== '' ? formatDateISO(raw) : undefined;
    const offerDue = offer?.due_date != null && offer.due_date !== '' ? formatDateISO(offer.due_date) : undefined;
    const next = deriveDueDateFromPaymentFlowV2(v2, formDue ?? offerDue);
    if ((formDue ?? '') !== next) {
      setValue('due_date', next, { shouldValidate: true });
    }
  }, [balanceDueDate, depositFlow, getValues, manualBalanceDueDate, offer?.due_date, paymentDeadline, setValue]);

  const isV2FullPayment = depositFlow === 'full_payment';
  const isV2PaymentSchedule = depositFlow === 'payment_schedule';
  const showV2ManualFields = depositFlow === 'manual';
  const isChangedFromLeLiveOffer = getIsChangedFromLeLiveOffer({
    offer,
    nextItemType: itemType,
  });

  const syncCustomerPaymentFromAmount = useCallback(
    (amount: number) => {
      const normalized = floatify(amount);
      const currency = getValues('price_currency');
      setValue('price', normalized, { shouldValidate: true, shouldDirty: true });
      setValue('cost_price', 0, { shouldValidate: true, shouldDirty: true });
      setValue('payment_metadata.final_amount_customer', normalized, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('payment_metadata.final_amount_vendor', 0, { shouldValidate: true, shouldDirty: true });
      setValue('payment_metadata.margin', 0, { shouldValidate: true, shouldDirty: true });
      setValue('cost_price_currency', currency, { shouldValidate: true, shouldDirty: true });
      setValue('payment_metadata.vendor_exchange_rate', 1, { shouldValidate: true, shouldDirty: true });
      clearErrors([
        'price',
        'cost_price',
        'payment_metadata.final_amount_customer',
        'payment_metadata.final_amount_vendor',
      ]);
    },
    [clearErrors, getValues, setValue],
  );

  const isActionsDisabled = isSubmitting || sendStatus === 'processing';
  const isSendDisabled = !inclusionsDiscussed || !termsExplained || isActionsDisabled;

  const showSendButton = !!offer?.id && !hasOrders;

  const derivedPaymentFlowV2Amounts = useMemo(
    () =>
      calculatePaymentFlowV2DerivedAmounts({
        paymentFlowType: depositFlow,
        price,
        depositAmount: v2DepositAmount,
        manualDepositAmount: manualV2DepositAmount,
      }),
    [depositFlow, manualV2DepositAmount, price, v2DepositAmount],
  );

  useEffect(() => {
    if (depositFlow === 'manual') {
      setValue(
        'payment_metadata.payment_flow_v2.manual_balance_amount',
        derivedPaymentFlowV2Amounts.manual_balance_amount,
      );
      clearErrors('payment_metadata.payment_flow_v2.manual_balance_amount');
      return;
    }

    if (depositFlow === 'full_payment' || depositFlow === 'payment_schedule') {
      setValue('payment_metadata.payment_flow_v2.balance_amount', derivedPaymentFlowV2Amounts.balance_amount);
      clearErrors('payment_metadata.payment_flow_v2.balance_amount');
    }
  }, [clearErrors, depositFlow, derivedPaymentFlowV2Amounts, setValue]);

  const onSubmit: SubmitHandler<CustomOfferFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = buildPayload(user, formData);
        payload.is_changed_from_le_live_offer = isChangedFromLeLiveOffer;
        payload.images = offer?.id ? (offer.images ?? []) : [LE_LIVE_CUSTOM_OFFER_IMAGE_ID];

        const response = offer?.id
          ? await OffersService.updateCustomOffer(offer.id, payload)
          : await OffersService.createCustomOffer(payload);

        const updatedOffer = response.result as App.CustomOffer;

        setOffer(updatedOffer);

        if (onUpdate) {
          await onUpdate(updatedOffer);
        }

        enqueueSnackbar(
          offer?.id ? 'Custom quote has been successfully updated' : 'Custom quote has been successfully created',
          { variant: 'success' },
        );

        replace(`/users/${user.id_member}/custom-offers/${updatedOffer.id}`);
      } catch (err) {
        enqueueSnackbar(`Can't process custom quote: ${err.message}`, { variant: 'error' });
      }
    },
    [user, isChangedFromLeLiveOffer, offer, onUpdate, enqueueSnackbar, replace],
  );

  const onSendPayment = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!offer?.id) {
      return;
    }

    try {
      setSendStatus('processing');

      const dueDate = getValues('due_date');
      if (isBefore(dueDate)) {
        throw new Error('Due date must be in the future');
      }

      const offerResponse = await OffersService.setCustomOfferStatus(offer.id, 'approved');
      const updatedOffer = offerResponse.result as App.CustomOffer;

      setOffer(updatedOffer);

      const payload = await buildPaymentPayload(user, updatedOffer, brand);

      const response = await OrdersService.createOrder(payload);

      if (offer.notes && offer.notes.length > 0) {
        await OrdersService.addOrderNote(response.result.id_orders, {
          comment: offer.notes,
          comment_type: OPERATIONS_SUPPORT,
        });
      }

      enqueueSnackbar('Payment request has been successfully created', { variant: 'success' });
      setSendStatus('success');

      setTimeout(() => window.location.reload(), 200);
    } catch (err) {
      setSendStatus('failed');
      enqueueSnackbar(`Can't create payment request: ${err.message}`, { variant: 'error' });
    }
  };

  const deleteOffer = async () => {
    if (!offer?.id) {
      return;
    }
    try {
      await OffersService.deleteCustomOffer(offer.id);
      push(`/users/${user.id_member}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      enqueueSnackbar(`Can't delete custom quote: ${message}`, { variant: 'error' });
    }
  };

  const handleOfferTypeChange = useCallback(
    (event: SelectChangeEvent<App.CustomOfferType>) => {
      const nextType = event.target.value as App.CustomOfferType;
      const currentInternalDiscountTracking = watch('payment_metadata.internal_discount_tracking');
      const currentVendorName = watch('item.vendor')?.trim() || '';
      setValue('supplier', DEFAULT_SUPPLIER);
      setValue('payment_metadata.sales_attribution', LE_LIVE_SALES_ATTRIBUTION);
      setValue('payment_metadata.block_customer_payment_notifications', true);
      setValue('item.type', nextType);
      changeFormType(
        nextType,
        currentInternalDiscountTracking,
        nextType === LE_LIVE_ITEM_TYPE ? undefined : currentVendorName,
      );
    },
    [setValue, watch, changeFormType],
  );

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} data-testid="custom-offer-form">
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={4}>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Customer: {user.fullName}
          </Typography>
          <Typography variant="h6" fontWeight="normal" color="GrayText">
            Email: {user.email}
          </Typography>
        </Stack>
      </Stack>
      <Box mt={2}>
        <Typography variant="h6">General offer information</Typography>
        <CustomOfferRequiredFieldsLegend />
      </Box>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.type"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel required>Offer type</InputLabel>
                <Select
                  {...field}
                  label="Offer type"
                  disabled={isOfferTypeSelectDisabled}
                  onChange={handleOfferTypeChange}
                  data-testid="offer-type"
                  renderValue={(value) => OFFER_TYPES.find((type) => type.value === value)?.label ?? String(value)}
                >
                  {offerTypeSelectOptions.map((offerType) => (
                    <MenuItem key={offerType.value} value={offerType.value}>
                      {offerType.label}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Vendor name"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                data-testid="vendor-name"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="item.vendor_reference_number"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Supplier booking reference"
                required
                helperText={error?.message}
                error={!!error}
                disabled={isSubmitting}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                data-testid="supplier-booking-reference"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="sold_at"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Sales date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting || hasOrders}
                fullWidth
                data-testid="sales-date"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid mt={2} columns={6} spacing={2} container>
        <Grid size={6}>
          <TextField
            {...register('item.name')}
            label="Offer name"
            required
            helperText={
              errors.item?.name
                ? errors.item.name.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            slotProps={{ inputLabel: { shrink: !!watch('item.name') } }}
            error={!!errors.item?.name}
            disabled={isSubmitting}
            fullWidth
            data-testid="offer-name"
          />
        </Grid>
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        <Grid size={{ xs: 3, md: 0.5 }}>
          <Controller
            name="item.start_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                label="Start date"
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting}
                fullWidth
                data-testid="start-date"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 0.5 }}>
          <Controller
            name="item.end_date"
            control={control}
            render={({ field: { ref: _, ...field }, fieldState: { error } }) => (
              <DateWidget
                {...field}
                slotProps={{ textField: { helperText: error?.message, error: !!error, required: true } }}
                disabled={isSubmitting}
                label="End date"
                minDate={watch(`item.start_date`) ? newDate(watch(`item.start_date`)) : undefined}
                data-testid="end-date"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid mt={2} columns={4} spacing={2} container>
        <Grid size={4}>
          <RichTextEditor
            {...register('item.description')}
            control={control}
            label="Description"
            required
            helperText={
              errors.item?.description
                ? errors.item.description.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.description}
            disabled={isSubmitting}
            data-testid="description"
          />
        </Grid>
        <Grid size={4}>
          <RichTextEditor
            {...register('item.service_information')}
            control={control}
            label="Terms & Conditions"
            required
            helperText={
              errors.item?.service_information
                ? errors.item.service_information.message
                : 'This is a customer-facing field and will be visible on customer portal and emails'
            }
            error={!!errors.item?.service_information}
            disabled={isSubmitting}
            data-testid="terms-and-conditions"
          />
        </Grid>
      </Grid>
      <TravellersSection
        ref={travellersSectionRef}
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        isSubmitting={isSubmitting}
        isBusinessTraveller={isBusinessTraveller}
        isEditMode={isEditMode}
        businessEmployees={[]}
        customerHasPaid={customerHasPaid}
        itemType={itemType}
        isTravellerCountryRequired={false}
      />
      <Box mt={2}>
        <Typography variant="h6">Customer payment</Typography>
      </Box>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="price_currency"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error} disabled={isSubmitting || hasOrders}>
                <InputLabel>Customer currency</InputLabel>
                <Select
                  {...field}
                  label="Customer currency"
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    setValue('cost_price_currency', value);
                    const amt = Number(getValues('payment_metadata.final_amount_customer'));
                    if (!hasOrders && Number.isFinite(amt) && amt > 0) {
                      syncCustomerPaymentFromAmount(amt);
                    }
                  }}
                  data-testid="customer-currency"
                >
                  {LE_LIVE_CUSTOMER_CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText>{error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 1 }}>
          <Controller
            name="payment_metadata.final_amount_customer"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                label="Final amount (customer)"
                type="number"
                fullWidth
                required
                disabled={isSubmitting || hasOrders}
                error={!!error}
                helperText={error?.message}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 0.01, min: 0 } }}
                value={typeof field.value !== 'number' || Number.isNaN(field.value) ? '' : field.value}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    field.onChange('' as unknown as number);
                    return;
                  }
                  const num = parseFloat(raw);
                  if (!Number.isFinite(num)) {
                    return;
                  }
                  const normalized = floatify(num);
                  field.onChange(normalized);
                  if (!hasOrders) {
                    syncCustomerPaymentFromAmount(normalized);
                  }
                }}
                data-testid="final-amount-customer"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3, md: 2 }}>
          <Controller
            name="payment_metadata.payment_flow_v2.type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Payment flow"
                disabled={hasOrders || isSubmitting}
                select
                fullWidth
                data-testid="deposit-flow"
              >
                <MenuItem value="full_payment">Full payment</MenuItem>
                <MenuItem value="payment_schedule">Deposit (customer pays via My Escapes)</MenuItem>
                <MenuItem value="manual">Deposit (manual - agent takes deposit)</MenuItem>
              </TextField>
            )}
          />
        </Grid>
      </Grid>
      <V2CustomerPaymentFields
        control={control}
        register={register}
        paymentFlowV2Errors={paymentFlowV2Errors}
        hasOrders={hasOrders}
        isSubmitting={isSubmitting}
        isV2FullPayment={isV2FullPayment}
        isV2PaymentSchedule={isV2PaymentSchedule}
        showV2ManualFields={showV2ManualFields}
      />
      <Grid mt={2} container columns={6} spacing={2} alignItems="flex-start">
        <CustomOfferInternalDiscountField
          control={control}
          errors={errors}
          isSubmitting={isSubmitting}
          gridSize={{ xs: 6, md: 2 }}
          textFieldSize="small"
        />
        <Grid size={{ xs: 6, md: 2 }}>
          <CustomOfferMerchantFeeField
            isBusinessTraveller={isBusinessTraveller}
            userId={user.id_member}
            offerId={offer?.id}
            control={control}
            setValue={setValue}
            hasOrders={hasOrders}
            isSubmitting={isSubmitting}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <CustomOfferLoyaltyCalculator
            productType={itemType}
            sellPrice={customerSellForLoyalty}
            currency={priceCurrencyCode}
            margin={0}
            leServiceFee={0}
            userId={user?.id_member}
            salesAttribution={LE_LIVE_SALES_ATTRIBUTION}
            brand={brand}
          />
        </Grid>
      </Grid>
      <Grid mt={2} container columns={6} spacing={2}>
        <Grid size={6}>
          <TextField
            {...register('notes')}
            helperText={errors.notes?.message}
            error={!!errors.notes}
            disabled={isSubmitting}
            label="Internal notes"
            minRows={2}
            fullWidth
            multiline
            data-testid="internal-notes"
          />
        </Grid>
      </Grid>
      {order?.id_orders && (
        <OrderDetailNotes
          orderId={order?.id_orders}
          customer={user}
          refreshData={() => {
            setTimeout(() => window.location.reload(), 200);
          }}
          offerId={offer?.id}
        />
      )}
      {isEditMode && (
        <Box mt={2}>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={inclusionsDiscussed}
                  onChange={(e) => setInclusionsDiscussed(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="inclusions-discussed"
                />
              }
              label="Inclusions discussed"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsExplained}
                  onChange={(e) => setTermsExplained(e.target.checked)}
                  disabled={hasOrders || isSubmitting}
                  data-testid="terms-explained"
                />
              }
              label="Terms and Conditions explained"
            />
          </FormGroup>
        </Box>
      )}
      <Stack mt={2} direction="row" justifyContent="space-between" spacing={2}>
        <Stack direction="row" spacing={2}>
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            disabled={isActionsDisabled}
            data-testid="save-button"
          >
            Save
          </Button>

          {showSendButton && (
            <Button
              type="button"
              startIcon={<CheckIcon />}
              variant="contained"
              color="secondary"
              disabled={isSendDisabled}
              onClick={onSendPayment}
              data-testid="confirm-button"
            >
              Confirm and show in my escapes
            </Button>
          )}
        </Stack>
        {offer?.status === 'draft' && (
          <Stack direction="row" spacing={2}>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={isActionsDisabled}
              onClick={openDeleteDialog}
              data-testid="delete-button"
            >
              Delete
            </Button>
          </Stack>
        )}
        <ConfirmDeleteDialog
          isOpen={deleteDialogOpen}
          description={offer?.name}
          handleClose={closeDeleteDialog}
          handleDelete={deleteOffer}
        />
      </Stack>
    </form>
  );
}
